/**
 * Push Notification Service for ASN Web App
 * Handles subscription, permission requests, and notification management
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// VAPID public key will be generated on backend
let vapidPublicKey = null;

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check current notification permission status
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return { success: false, error: 'Notifications not supported' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get VAPID public key from backend
 */
export const getVapidPublicKey = async () => {
  if (vapidPublicKey) return vapidPublicKey;
  
  try {
    const response = await fetch(`${API}/notifications/vapid-public-key`);
    if (response.ok) {
      const data = await response.json();
      vapidPublicKey = data.publicKey;
      return vapidPublicKey;
    }
  } catch (error) {
    console.error('Failed to get VAPID key:', error);
  }
  return null;
};

/**
 * Convert VAPID key to Uint8Array
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Subscribe user to push notifications
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push not supported' };
  }

  try {
    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Get VAPID key
      const publicKey = await getVapidPublicKey();
      if (!publicKey) {
        return { success: false, error: 'Could not get VAPID key' };
      }

      // Subscribe
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }

    // Send subscription to backend
    const token = localStorage.getItem('userToken');
    const userId = (() => {
      try {
        const userData = localStorage.getItem('userData');
        return userData ? JSON.parse(userData).id || '' : '';
      } catch { return ''; }
    })();
    const response = await fetch(`${API}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
        user_id: userId
      })
    });

    if (response.ok) {
      localStorage.setItem('push_subscribed', 'true');
      return { success: true, subscription };
    } else {
      return { success: false, error: 'Failed to save subscription' };
    }
  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      
      // Notify backend
      await fetch(`${API}/notifications/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });
    }
    
    localStorage.removeItem('push_subscribed');
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Check if user is subscribed to push
 */
export const isSubscribedToPush = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

/**
 * Register service worker with update handling
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return { success: false, error: 'Service workers not supported' };
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Always check for updates
    });
    
    console.log('[ASN] Service worker registered:', registration.scope);
    
    // Check for updates on registration
    registration.update();
    
    // Handle controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[ASN] New service worker activated, refreshing page');
      // Automatically reload to get fresh content
      window.location.reload();
    });
    
    return { success: true, registration };
  } catch (error) {
    console.error('[ASN] Service worker registration failed:', error);
    return { success: false, error: error.message };
  }
};
